import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Viewport width at/above which the drawer sits alongside the header rather than as a full-height modal. */
  #desktopQuery = window.matchMedia('(min-width: 990px)');

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    window.removeEventListener('scroll', this.#updateDrawerTop);
    window.removeEventListener('resize', this.#updateDrawerTop);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#updateDrawerTop();
    window.addEventListener('scroll', this.#updateDrawerTop, { passive: true });
    window.addEventListener('resize', this.#updateDrawerTop);

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleDrawerClose = () => {
    window.removeEventListener('scroll', this.#updateDrawerTop);
    window.removeEventListener('resize', this.#updateDrawerTop);
  };

  /**
   * Anchors the drawer's top edge to the live bottom of the cart trigger icon, so it
   * opens directly underneath the icon — covering any secondary nav row beneath it —
   * rather than under a static `--header-height` offset, which measures only the sticky
   * header's own height and can misplace the drawer relative to the icon it hangs from.
   * No-op in modal mode (mobile), which stays full height.
   */
  #updateDrawerTop = () => {
    if (!this.#desktopQuery.matches) return;

    const dialog = this.#dialog;
    const drawerId = this.#themeDrawer?.id;
    const trigger = drawerId ? document.querySelector(`[aria-controls="${drawerId}"]`) : null;
    if (!dialog || !trigger) return;

    const bottom = Math.round(trigger.getBoundingClientRect().bottom);

    // When the trigger has scrolled out of view above the viewport (e.g. adding from
    // the sticky add-to-cart while a `scroll-up` header is hidden), pinning to a clamped
    // 0 would give the drawer `top: 0` and make it full-height like the mobile modal.
    // Clear the override instead so the CSS falls back to `--header-height` and the
    // drawer stays a header-anchored dropdown.
    if (bottom <= 0) {
      dialog.style.removeProperty('--cart-drawer-top');
      return;
    }

    dialog.style.setProperty('--cart-drawer-top', `${bottom}px`);
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Intentionally do NOT open here. The event fires the moment the add starts
    // (before the fetch), so opening now would reveal an empty/stale drawer that
    // then flickers — or gets closed by the hover-idle timer — before the item
    // arrives. We wait for event.promise (below), which resolves once the add
    // request has completed and the drawer content has re-rendered, then open.

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            // On desktop the auto-opened drawer behaves like a hover preview that fades
            // after a delay, so it must NOT steal keyboard focus: if focus were parked on
            // a control inside the panel, the fade would detach the panel out from under a
            // keyboard/screen-reader user and orphan their focus (WCAG 2.4.3). Focus stays
            // on the add-to-cart trigger; the shopper can Tab into the drawer to engage it,
            // which fires focusin → openOnHover and cancels the fade. On mobile it's a
            // full-height modal that stays open, so keep the native focus-to-close-button.
            this.#themeDrawer?.open({ focus: !this.#desktopQuery.matches });
          }
          // Start the idle "fade away" timer so an auto-opened drawer dismisses itself
          // like a hover preview — otherwise a committed open stays up until the pointer
          // leaves the trigger/panel, which never happens when adding from the sticky
          // add-to-cart (pointer stays at the bottom, the header trigger is off-screen).
          // Hovering or focusing the panel cancels this via openOnHover. No-op on mobile.
          this.#themeDrawer?.scheduleHoverClose();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
